import { db, functions } from '@/firebase'
import { browserReportSync } from '@/utils/browser-report.js'

const ACTIONS = {
  SIGNUP: 'signup',
  SIGNIN: 'signin',
  RESET_PASSWORD: 'reset_password',
  LOGOUT: 'logout',
  EMAIL_VERIFIED: 'email_verified',
  CREATED_PROJECT: 'created project',
  DUPLICATED_PROJECT: 'duplicated project',
  PUBLISHED_PROJECT: 'published project',
  DELETED_PROJECT: 'deleted project',
  DUPLICATED_EXPERIENCE: 'duplicated experience',
  APPLIED_TEMPLATE: 'applied template',
  UPGRADE_SCREEN: 'upgrade screen',
  UPGRADED_ACCOUNT: 'upgraded account',
  CANCEL_PLAN: 'cancel plan',
  ADDED_CARD: 'added card',
  REMOVED_CARD: 'removed card',
  ADDED_DOMAIN: 'added domain',
  REMOVED_DOMAIN: 'removed domain',
  ADDED_INTEGRATION: 'added integration',
  REMOVED_INTEGRATION: 'removed integration',
  UPGRADED_APP: 'upgraded app'
}

const ACTION_TYPES = {
  AUTH: 'auth',
  BUILDER: 'builder',
  ACCOUNT: 'account',
  DOMAIN: 'domain',
  INTEGRATIONS: 'integrations'
}

const logAction = async (actionData) => {
  const getRequestIpAddress = functions.httpsCallable('getRequestIpAddress')
  const userAgent = browserReportSync()
  const { data } = await getRequestIpAddress()

  db()
    .ref('logs')
    .push({
      ...actionData,
      ip: data.ip,
      user_agent: userAgent,
      device: `${userAgent.browser.name} / ${userAgent.browser.version} on ${userAgent.os.name} / ${userAgent.os.version}`,
      log_version: 2
    })
}

const signupAction = async (user = null, success = true) => {
  logAction({
    action: ACTIONS.SIGNUP,
    action_type: ACTION_TYPES.AUTH,
    username: user ? user.username : '',
    email: user ? user.email : '',
    user_uid: user ? user.uid : '',
    success
  })
}

const signinAction = async (user = null, success = true) => {
  logAction({
    action: ACTIONS.SIGNIN,
    action_type: ACTION_TYPES.AUTH,
    username: user ? user.username : '',
    email: user ? user.email : '',
    user_uid: user ? user.uid : '',
    success
  })
}

const logoutAction = async (user = null, success = true) => {
  logAction({
    action: ACTIONS.LOGOUT,
    action_type: ACTION_TYPES.AUTH,
    username: user ? user.username : '',
    email: user ? user.email : '',
    user_uid: user ? user.uid : '',
    success
  })
}

const resetPasswordAction = async (user = null, success = true) => {
  logAction({
    action: ACTIONS.RESET_PASSWORD,
    action_type: ACTION_TYPES.AUTH,
    username: user ? user.username : '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    success
  })
}

const emailVerifiedAction = async (user = null, success = true) => {
  logAction({
    action: ACTIONS.EMAIL_VERIFIED,
    action_type: ACTION_TYPES.AUTH,
    username: user ? user.username : '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    success
  })
}

const createdProjectAction = async (user = null, project = {}, success = true) => {
  logAction({
    action: ACTIONS.CREATED_PROJECT,
    action_type: ACTION_TYPES.BUILDER,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    success,
    ...project
  })
}

const duplicatedProjectAction = async (user = null, project = {}, success = true) => {
  logAction({
    action: ACTIONS.DELETED_PROJECT,
    action_type: ACTION_TYPES.BUILDER,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...project,
    success
  })
}

const duplicatedExperienceAction = async (user = null, project = {}, success = true) => {
  logAction({
    action: ACTIONS.DUPLICATED_EXPERIENCE,
    action_type: ACTION_TYPES.BUILDER,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...project,
    success
  })
}

const publishedProjectAction = async (user = null, project = {}, success = true) => {
  logAction({
    action: ACTIONS.PUBLISHED_PROJECT,
    action_type: ACTION_TYPES.BUILDER,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...project,
    success
  })
}

const deletedProjectAction = async (user = null, project = {}, success = true) => {
  logAction({
    action: ACTIONS.DELETED_PROJECT,
    action_type: ACTION_TYPES.BUILDER,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...project,
    success
  })
}

const appliedTemplateAction = async (user = null, template = {}, success = true) => {
  logAction({
    action: ACTIONS.APPLIED_TEMPLATE,
    action_type: ACTION_TYPES.BUILDER,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...template,
    success
  })
}

const upgradedAccountAction = async (user = null, planChange = {}, success = true) => {
  logAction({
    action: ACTIONS.UPGRADED_ACCOUNT,
    action_type: ACTION_TYPES.ACCOUNT,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...planChange,
    success
  })
}

const upgradedScreenAction = async (user = null, planChange = {}, success = true) => {
  logAction({
    action: ACTIONS.UPGRADE_SCREEN,
    action_type: ACTION_TYPES.ACCOUNT,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...planChange,
    success
  })
}

const cancelPlanAction = async (user = null, planChange = {}, success = true) => {
  logAction({
    action: ACTIONS.CANCEL_PLAN,
    action_type: ACTION_TYPES.ACCOUNT,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...planChange,
    success
  })
}

const addedCardAction = async (user = null, details = {}, success = true) => {
  logAction({
    action: ACTIONS.ADDED_CARD,
    action_type: ACTION_TYPES.ACCOUNT,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...details,
    success
  })
}

const removedCardAction = async (user = null, details = {}, success = true) => {
  logAction({
    action: ACTIONS.REMOVED_CARD,
    action_type: ACTION_TYPES.ACCOUNT,
    username: (user && user.username) || '',
    email: user ? user.email : '',
    user_uid: user ? user.uid : '',
    ...details,
    success
  })
}

const upgradedAppAction = async (user = null, version = {}, success = true) => {
  logAction({
    action: ACTIONS.UPGRADED_APP,
    action_type: ACTION_TYPES.ACCOUNT,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...version,
    success
  })
}

const addedDomainAction = async (user = null, domain = {}, success = true) => {
  logAction({
    action: ACTIONS.ADDED_DOMAIN,
    action_type: ACTION_TYPES.DOMAIN,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...domain,
    success
  })
}

const removedDomainAction = async (user = null, domain = {}, success = true) => {
  logAction({
    action: ACTIONS.REMOVED_DOMAIN,
    action_type: ACTION_TYPES.DOMAIN,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...domain,
    success
  })
}
const addedIntegrationAction = async (user = null, integration = {}, success = true) => {
  logAction({
    action: ACTIONS.ADDED_INTEGRATION,
    action_type: ACTION_TYPES.INTEGRATIONS,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...integration,
    success
  })
}

const removedIntegrationAction = async (user = null, integration = {}, success = true) => {
  logAction({
    action: ACTIONS.REMOVED_INTEGRATION,
    action_type: ACTION_TYPES.INTEGRATIONS,
    username: (user && user.username) || '',
    email: (user && user.email) || '',
    user_uid: user ? user.uid : '',
    ...integration,
    success
  })
}

export {
  signupAction,
  signinAction,
  resetPasswordAction,
  logoutAction,
  emailVerifiedAction,
  createdProjectAction,
  duplicatedProjectAction,
  duplicatedExperienceAction,
  publishedProjectAction,
  deletedProjectAction,
  appliedTemplateAction,
  upgradedAccountAction,
  upgradedScreenAction,
  cancelPlanAction,
  addedCardAction,
  removedCardAction,
  addedDomainAction,
  removedDomainAction,
  addedIntegrationAction,
  removedIntegrationAction,
  upgradedAppAction
}
